import type { ReactNode } from 'react';

import type { ApiScan } from '@kaiizen/kai-pro-api';
import type { ScanMutators } from '@kaiizen/kai-pro-api-react-state';
import type { ScanRootOptimized } from '@kaiizen/kai-scan-template-system';

import { ANALYTICS_EVENTS, trackEvent } from '@/Client/analytics';
import type { ProjectLifecycleStateType } from '@/Client/Layout';
import { inDebugMode } from '@/Client/Lib';
import { ApproveCloseoutHeaderAction } from '@/Client/Partials/CloseoutReviewFlow';
import { getProjectUrl } from '@/Client/State/kaiNavigation';
import { navigateTo } from '@/Client/State/navigationRef';
import { NotificationBell } from '@/Client/Widgets/NotificationBell';
import { ProfileDropdownMenu } from '@/Client/Widgets/ProfileDropdownMenu';
import { NotificationIcon } from '@kaiizen/kai-shiki/react';
import { ApproveAndPublishHeaderAction } from './ApproveAndPublishHeaderAction';
import { CancelChangeOrderHeaderAction } from './CancelChangeOrderHeaderAction';
import { DebugButtonsHeaderAction } from './DebugButtonsHeaderAction';
import { DispatchHeaderAction } from './DispatchHeaderAction';
import { SubmitScopeHeaderAction } from './SubmitScopeHeaderAction';
import { TurnHandoffHeaderAction } from './TurnHandoffHeaderAction';

/**
 * The bag of data a buttonAction resolver may pull from - deliberately generic
 * scan-editing primitives, not named callbacks per button. Adding a new button
 * should only ever require a new entry in DEFAULT_MAIN_NAVIGATION_CONFIG below,
 * never a new field here, unless that button needs a primitive not already
 * present.
 */
export type MainNavigationActionArgs = {
  state: ProjectLifecycleStateType;
  scan?: ApiScan;
  scanMutators: ScanMutators;
  /** Debug-only tools (download/log the d-optimized scan) - optional
   *  since they're only ever populated by the one caller (AdminScanPageNew)
   *  that has them; the debugTools buttonAction below no-ops without them. */
  scanRoot?: ScanRootOptimized;
};

export type MainNavigationActionConfig = {
  id: string;
  buttonAction: (args: MainNavigationActionArgs) => ReactNode | undefined;
};

export type MainNavigationConfig = {
  actions: readonly MainNavigationActionConfig[];
  onAddressClick: (args?: MainNavigationActionArgs) => void;
};

/**
 * Defines both which top-bar action buttons exist and when each renders. Each
 * buttonAction is the single source of truth for both "is this relevant right
 * now" and "what does it look like" - the same shape as
 * projectStageStepperConfig's sub resolvers, just returning a ReactNode
 * instead of a string.
 */
export const DEFAULT_MAIN_NAVIGATION_CONFIG: MainNavigationConfig = {
  actions: [
    {
      id: 'cancelChangeOrder',
      buttonAction: args => {
        // Same broader availability as the legacy Cancel Change Order button
        // (ScopeToolbar.tsx / ScanReview.tsx): visible for the whole life of an
        // unsettled change order, not just while it's a draft. A freshly
        // vivified change order can still read phase 'edit' for a moment (its
        // scanIsChangeOrder/scanScopeState diffing hasn't caught up to the
        // scan's own scanType yet - see useProjectStateLifecycle), so this
        // mounts across 'edit', 'approved' (drafting), and 'review' (submitted,
        // pending admin review) alike - excluding only 'closeout', which a
        // change order scan can't be in - and leaves the real decision to the
        // own useCancelChangeOrderAction hook (canCancel, backed by the real
        // scan.scanType/scanLifecycleStatus) rather than re-deriving an
        // approximation of that check here.
        return (args.state.phase === 'edit' || args.state.phase === 'approved' || args.state.phase === 'review')
          ? <CancelChangeOrderHeaderAction />
          : undefined;
      },
    },
    {
      id: 'dispatchInitialWalk',
      buttonAction: args => {
        // Dispatch (assigning field agent(s) to walk the property) is only
        // ever a draft-phase concern - once the initial scope is approved, the
        // project's had its first walk one way or another. Mounts for the
        // whole of 'edit' and lets its own useDispatchAction hook (agent
        // assignments, whether scope work has started) decide the rest: plain
        // Dispatch remains the legacy action. With turn-based handoff enabled,
        // DispatchHeaderAction hides itself and initial handoff performs dispatch.
        return args.state.phase === 'edit' ? <DispatchHeaderAction /> : undefined;
      },
    },
    {
      id: 'submitScope',
      buttonAction: args => {
        // A change order draft, or all work complete but closeout not yet
        // submitted, both stay in 'approved' phase (see useProjectStateLifecycle) -
        // mount the component in either 'edit' or 'approved' and let its own
        // useScopeSubmissionAction hook (canSubmit/scanTypeLabel, backed by the
        // real scanIsSubmittable/effectiveScanType) decide whether anything is
        // actually submittable right now, rather than re-deriving an
        // approximation of that check here.
        return (args.state.phase === 'edit' || args.state.phase === 'approved') ? <SubmitScopeHeaderAction /> : undefined;
      },
    },
    {
      id: 'turnHandoff',
      // The component keeps handoff draft-only, but lets an assigned project
      // member take responsibility at every active lifecycle stage. Mount it
      // here so review and Closeout cannot hide the takeover control.
      buttonAction: () => <TurnHandoffHeaderAction />,
    },
    {
      id: 'approveAndPublish',
      buttonAction: args => {
        if (args.state.phase === 'review') return <ApproveAndPublishHeaderAction />;
        if (args.state.phase === 'edit' || args.state.phase === 'approved') return <ApproveAndPublishHeaderAction allowTurnBasedDraftApproval />;
        return undefined;
      },
    },
    {
      id: 'approveCloseout',
      buttonAction: args => {
        const isCloseout = args.state.phase === 'closeout';
        const isTurnBasedCloseoutReadyForApproval = args.state.phase === 'approved' && args.state.workComplete;
        // Once closeout is actually approved, phase 'closeout' persists forever
        // (see useProjectStateLifecycle) so ProjectStageStepper can keep showing
        // "Closeout approved" on later visits - but there's nothing left to
        // approve at that point, so this button should stop rendering entirely.
        // Checks !projectIsEditable (project-level, permanent) rather than only
        // closeoutApproved (this scan's own scanLifecycleStatus): approving a
        // closeout scan server-side auto-creates a new 'progress' child scan
        // that starts back at scanLifecycleStatus 'dispatched', which would make
        // closeoutApproved misleadingly false for that scan even though the
        // project itself is permanently done.
        if (isCloseout && (args.state.closeoutApproved || !args.state.projectIsEditable)) return undefined;
        // Turn-based Closeout does not use the legacy submit-for-review step:
        // once work is complete, the designated Project Manager approves it
        // directly, whether or not they currently hold the turn. The action
        // component verifies the feature flag and project-manager designation.
        if ((!isCloseout && !isTurnBasedCloseoutReadyForApproval) || !args.scan) return undefined;
        const { id: scanId, propertyProjectId } = args.scan;
        return (
          <ApproveCloseoutHeaderAction
            allowTurnBasedDirectApproval={isTurnBasedCloseoutReadyForApproval}
            newScopeBuilderFlow={true}
            onApproveCloseout={async () => {
              await args.scanMutators.update(scanId, { scanLifecycleStatus: 'approved' });
              trackEvent(ANALYTICS_EVENTS.scanApproved, { scanId, phase: 'closeout' });
              navigateTo(getProjectUrl(propertyProjectId));
            }}
            onRequestCloseoutEdit={async () => {
              await args.scanMutators.update(scanId, { scanLifecycleStatus: 'correctionsRequested' });
              trackEvent(ANALYTICS_EVENTS.scanChangesRequested, { scanId, phase: 'closeout' });
            }}
          />
        );
      },
    },
    {
      id: 'notifications',
      buttonAction: () => <NotificationBell icon={<NotificationIcon size={18} />} />,
    },
    {
      id: 'profile',
      buttonAction: () => <ProfileDropdownMenu />,
    },
    {
      id: 'debugTools',
      buttonAction: args => {
        const { scan, scanRoot } = args;
        if (!inDebugMode() || !scanRoot) return undefined;
        return <DebugButtonsHeaderAction scanRoot={scanRoot} scanName={scan?.name} />;
      },
    },
  ],
  onAddressClick: args => args?.scan?.propertyProjectId && navigateTo(getProjectUrl(args.scan.propertyProjectId)),
};
